import {
	FeedbackReport,
	FeedbackReportAuthorKind,
	FeedbackReportBoard,
	FeedbackReportBoardSource,
	FeedbackReportBuild,
	FeedbackReportComment,
	FeedbackReportEnv,
	FeedbackReportKind,
	FeedbackReportOrigin,
	FeedbackReportOriginKind,
	FeedbackReportPriority,
	FeedbackReportSource,
	FeedbackReportStatus,
	FEEDBACK_REPORT_COMMENT_MAX_LENGTH,
	FEEDBACK_REPORT_STATUSES,
	isFeedbackReportClosed,
} from '@run-diffusion/shared';

// The Feedback admin page's rules, apart from the page: what each status, kind and priority is
// called, which reports each view and filter shows, how they sort, what a triage write sends
// (shaped as firestore.rules requires), and the report as text for a developer's agent.

export const STATUS_LABELS: Record<FeedbackReportStatus, string> = {
	new: 'New',
	triaged: 'Triaged',
	needs_info: 'Needs info',
	approved: 'Approved',
	progress: 'In progress',
	fixed: 'Fixed', // the fix is in a pull request
	merged: 'Merged',
	deployed: 'Deployed',
	wontfix: 'Won\'t fix',
	duplicate: 'Duplicate',
};

export const KIND_LABELS: Record<FeedbackReportKind, string> = {
	bug: 'Bug',
	confusing: 'Confusing',
	idea: 'Idea',
};

// The board asks each kind its own two questions (web/board/src/ui/feedback/FeedbackDialog.vue);
// happened and expected hold the answers. These head them, in the third person.
export const KIND_QUESTIONS: Record<FeedbackReportKind, { happened: string, expected: string }> = {
	bug: { happened: 'What happened', expected: 'What they expected' },
	confusing: { happened: 'What they were trying to do', expected: 'What got in the way' },
	idea: { happened: 'What they would like', expected: 'What it would help them do' },
};

// Where the tester opened Feedback from on the board, or that a developer's agent filed it, or
// that it was imported from a list.
export const SOURCE_LABELS: Record<FeedbackReportSource, string> = {
	button: 'Feedback button',
	menu: 'menu',
	key: 'shortcut key',
	agent: 'agent',
	import: 'list',
};

const BOARD_SOURCES: FeedbackReportBoardSource[] = ['button', 'menu', 'key'];

// A report a developer's agent filed ('agent') or imported from a list ('import'), or null for
// one a tester sent from a board. Its origin says which; source says the same, should it lack one.
export const originKind = (report: FeedbackReport): FeedbackReportOriginKind | null => {
	if (report.origin?.kind) return report.origin.kind;
	return (BOARD_SOURCES as FeedbackReportSource[]).includes(report.source) ? null : report.source as FeedbackReportOriginKind;
};

// The badge on a report an agent filed or imported.
export const ORIGIN_LABELS: Record<FeedbackReportOriginKind, string> = {
	agent: 'Agent',
	import: 'Imported',
};

// How it arrived, as "sent 3 hours ago from the board's Feedback button", "filed 3 hours ago" (by
// the agent the page names beside it) or "imported 3 hours ago from Boards v2 sheet".
export const arrival = (report: FeedbackReport): { verb: string, via: string } => {
	const kind: FeedbackReportOriginKind | null = originKind(report);
	if (kind === 'agent') return { verb: 'filed', via: '' };
	if (kind === 'import') return { verb: 'imported', via: report.origin?.list ? `from ${report.origin.list}` : 'from a list' };
	return { verb: 'sent', via: `from the board's ${SOURCE_LABELS[report.source] || SOURCE_LABELS.button}` };
};

// A link the page may open: http(s) only, so a list's text can't become a script link.
export const httpLink = (value: string | null | undefined): string | null => {
	const link: string = (value || '').trim();
	return /^https?:\/\/\S+$/i.test(link) ? link : null;
};

// The screen it was sent from, unless unknown: an agent that names no viewport sends 0x0.
export const hasViewport = (env: FeedbackReportEnv | null | undefined): env is FeedbackReportEnv =>
	!!env && env.viewport?.w > 0 && env.viewport?.h > 0;

// What a report knows of where it was sent from. One an agent filed or imported comes from no
// board, and may not know its build or browser: the feedback API writes empty placeholders then
// (a board with no id, a build with no version or commit, an environment with no browser), so a
// page that dereferences them still draws it, and reports filed before it did hold null. Either
// way, it is absent, as the feedback API reads it too (has_board, has_build and has_env in
// restServer apiv2/feedback_service.py). A board always names the browser, if only as "Unknown
// browser".
export const hasBoard = (board: FeedbackReportBoard | null | undefined): board is FeedbackReportBoard =>
	!!board?.id;

export const hasBuild = (build: FeedbackReportBuild | null | undefined): build is FeedbackReportBuild =>
	!!build && !!(build.version || build.commit);

export const hasEnv = (env: FeedbackReportEnv | null | undefined): env is FeedbackReportEnv =>
	!!env?.browser;

export const PRIORITY_LABELS: Record<FeedbackReportPriority, string> = {
	p0: 'P0 Urgent',
	p1: 'P1 High',
	p2: 'P2 Normal',
	p3: 'P3 Low',
};

// What a status asks for before it is set: a question, a reason, or the report it repeats. The
// note lands on the status change's line in the comments.
export interface StatusNoteCopy {
	title: string
	help: string
	noteLabel: string
	confirm: string
	noteRequired: boolean
}

export const STATUS_NOTE_COPY: Partial<Record<FeedbackReportStatus, StatusNoteCopy>> = {
	needs_info: {
		title: 'Ask for more',
		help: 'Say what you need to know. The question stays on the report, for whoever follows up with the reporter.',
		noteLabel: 'Your question',
		confirm: 'Ask',
		noteRequired: true,
	},
	wontfix: {
		title: 'Close as won\'t fix',
		help: 'Nobody will work on it. Say why, so the next person to read it knows.',
		noteLabel: 'Why not (optional)',
		confirm: 'Close it',
		noteRequired: false,
	},
	duplicate: {
		title: 'Close as a duplicate',
		help: 'Point it at the report it repeats. Work happens there.',
		noteLabel: 'A note (optional)',
		confirm: 'Close it',
		noteRequired: false,
	},
	merged: {
		title: 'Mark merged',
		help: 'Say where it was merged, if it helps the next person.',
		noteLabel: 'A note (optional)',
		confirm: 'Mark merged',
		noteRequired: false,
	},
	deployed: {
		title: 'Mark deployed',
		help: 'Say which build or deploy it went out in, so testers know what to retest.',
		noteLabel: 'Build or deploy (optional)',
		confirm: 'Mark deployed',
		noteRequired: false,
	},
};

export const statusNeedsNote = (status: FeedbackReportStatus): boolean => !!STATUS_NOTE_COPY[status];

// The page's tabs: reports waiting on a decision, approved work, fixes on their way out (in a
// pull request, or merged), and closed reports (deployed, or closed without a fix). The feedback
// API's views are the same (restServer apiv2/feedback_service.py).
export type FeedbackView = 'review' | 'approved' | 'shipping' | 'closed' | 'all';

export const VIEW_STATUSES: Record<Exclude<FeedbackView, 'all'>, FeedbackReportStatus[]> = {
	review: ['new', 'triaged', 'needs_info'],
	approved: ['approved', 'progress'],
	shipping: ['fixed', 'merged'],
	closed: ['deployed', 'wontfix', 'duplicate'],
};

// What the page keeps listening to in full: every report outside Closed. Closed ones are kept
// forever and outgrow any list, so only the newest of them are read.
export const LIVE_STATUSES: FeedbackReportStatus[] = FEEDBACK_REPORT_STATUSES.filter((status: FeedbackReportStatus) => !VIEW_STATUSES.closed.includes(status));

export const inView = (report: FeedbackReport, view: FeedbackView): boolean =>
	view === 'all' || VIEW_STATUSES[view].includes(report.status);

// Who sent it: a RunDiffusion admin, or anyone else. Reports from before the flag was kept count as users'.
export type ReporterFilter = 'all' | 'admin' | 'user';

export const isAdminReport = (report: FeedbackReport): boolean => report.reporter?.isAdmin === true;

export const matchesReporter = (report: FeedbackReport, filter: ReporterFilter): boolean =>
	filter === 'all' || (filter === 'admin') === isAdminReport(report);

export type KindFilter = 'all' | FeedbackReportKind;

export const matchesKind = (report: FeedbackReport, filter: KindFilter): boolean => filter === 'all' || report.kind === filter;

// A number ("12" or "#12") finds that report, and "12" also finds an imported row whose id in
// its list is 12. An imported row's id ("B12") finds it, whole; anything else searches its words,
// board, reporter, company, and the list it was imported from.
export const matchesSearch = (report: FeedbackReport, search: string): boolean => {
	const term: string = (search || '').trim().toLowerCase();
	if (!term) return true;
	const externalId: string = (report.origin?.externalId || '').trim().toLowerCase();
	if (externalId && externalId === term) return true;
	if (/^#?\d+$/.test(term)) return String(report.number) === term.replace('#', '');
	const board: FeedbackReportBoard | null = hasBoard(report.board) ? report.board : null;
	return [
		report.happened,
		report.expected,
		board?.title,
		board?.id,
		report.reporter?.email,
		report.reporter?.companyName,
		report.origin?.list,
	].some((value: string | null | undefined) => (value || '').toLowerCase().includes(term));
};

export type FeedbackSort = 'newest' | 'oldest' | 'priority';

const PRIORITY_RANK: Record<FeedbackReportPriority, number> = { p0: 0, p1: 1, p2: 2, p3: 3 };
// Unset sorts after p3.
const priorityRank = (report: FeedbackReport): number => {
	const priority: FeedbackReportPriority | null | undefined = report.triage?.priority;
	return priority ? PRIORITY_RANK[priority] : 4;
};

// Priority puts the most urgent first, unset last, and the newest first within each.
export const compareReports = (sort: FeedbackSort) => (a: FeedbackReport, b: FeedbackReport): number => {
	if (sort === 'oldest') return a.number - b.number;
	if (sort === 'priority') {
		const byPriority: number = priorityRank(a) - priorityRank(b);
		if (byPriority) return byPriority;
	}
	return b.number - a.number;
};

// A Firestore write's values: the page passes FieldValue.serverTimestamp() as `stamp`.
export type FeedbackUpdate = Record<string, unknown>;

// Any triage write: updatedAt is stamped, and a report closed before closedAt was kept gets one
// now (the rules want every closed report to have it).
export const triageUpdate = (report: FeedbackReport, changes: FeedbackUpdate, stamp: unknown): FeedbackUpdate => ({
	...changes,
	updatedAt: stamp,
	...(isFeedbackReportClosed(report.status) && !report.closedAt && !('closedAt' in changes) ? { closedAt: stamp } : {}),
});

// Who is working on a report: one person, or one developer's agent (FeedbackReportClaim).
export interface ReportHolder {
	// this exact claim (who, which device and session, since when): a take-over confirmed for one
	// claim never replaces another
	key: string
	uid: string
	name: string | null // null on a report from before claims were kept: look the person up
	kind: FeedbackReportAuthorKind
	agent: string | null // the agent's own name ("Claude Code"); null for a person
	since: Date | null
	assignedBy: string | null // User.id of the admin who assigned it to them; null when they claimed it themself
}

// Its claim, or, on a report from before claims were kept, its owner as a person.
export const currentHolder = (report: FeedbackReport): ReportHolder | null => {
	const claim = report.claim;
	if (claim?.uid) {
		return {
			key: [claim.kind, claim.uid, claim.deviceId || '', claim.session || '', claim.claimedAt ? claim.claimedAt.toMillis() : ''].join('|'),
			uid: claim.uid,
			name: claim.name || null,
			kind: claim.kind === 'agent' ? 'agent' : 'human',
			agent: claim.agent || null,
			since: claim.claimedAt ? claim.claimedAt.toDate() : null,
			assignedBy: claim.assignedBy && claim.assignedBy !== claim.uid ? claim.assignedBy : null,
		};
	}
	const owner: string | null | undefined = report.triage?.owner;
	return owner ? { key: `owner|${owner}`, uid: owner, name: null, kind: 'human', agent: null, since: null, assignedBy: null } : null;
};

// Held by someone other than this person: another admin, or any agent (their own included, so
// taking over from it is a choice they make, not a side effect).
export const heldByOther = (report: FeedbackReport, uid: string): ReportHolder | null => {
	const holder: ReportHolder | null = currentHolder(report);
	return holder && (holder.uid !== uid || holder.kind === 'agent') ? holder : null;
};

// The claim fields of a write: a person (this admin, or the admin `assignedBy` assigns it to), now,
// or nobody. firestore.rules wants triage.owner to follow the claim, a take-over to name the claim
// it replaces (`takeoverOf`: that claim's claimedAt), and an assignment to name who made it
// (`assignedBy`, never on a claim someone makes for themself).
const claimChanges = (holder: { uid: string; name: string } | null, stamp: unknown, takeoverOf: unknown = null, assignedBy: string | null = null): FeedbackUpdate => (holder
	? {
		claim: {
			uid: holder.uid,
			name: holder.name,
			kind: 'human',
			agent: null,
			deviceId: null,
			claimedAt: stamp,
			...(takeoverOf ? { takeoverOf } : {}),
			...(assignedBy && assignedBy !== holder.uid ? { assignedBy } : {}),
		},
		'triage.owner': holder.uid,
	}
	: { claim: null, 'triage.owner': null });

// Why a claim, take-over or assignment confirmed now can't be saved: the report is closed (a closed
// report takes no claim: firestore.rules refuses one, so reopen it first), even when this page's
// copy already shows it closed; or its status moved since the dialog opened. Null when it can go.
export const claimBlocked = (fresh: FeedbackReport, from: FeedbackReportStatus): 'closed' | 'moved' | null => {
	if (isFeedbackReportClosed(fresh.status)) return 'closed';
	return fresh.status !== from ? 'moved' : null;
};

// Claiming a report (taking it over from `takeoverOf`'s holder, when someone else holds it), or
// letting it go.
export const claimUpdate = (report: FeedbackReport, author: { uid: string; name: string } | null, stamp: unknown, takeoverOf: unknown = null): FeedbackUpdate =>
	triageUpdate(report, claimChanges(author, stamp, takeoverOf), stamp);

// Assigning a report to another system admin, `assignee`, as the admin `assignedBy`: they hold it
// as if they had claimed it, and the claim says who assigned it. Over a claim someone other than
// the assigning admin holds, it names that claim (`takeoverOf`), as taking one over does.
// Assigning it to yourself is claiming it, and writes no assignedBy.
export const assignUpdate = (report: FeedbackReport, assignee: { uid: string; name: string }, assignedBy: string, stamp: unknown, takeoverOf: unknown = null): FeedbackUpdate =>
	triageUpdate(report, claimChanges(assignee, stamp, takeoverOf, assignedBy), stamp);

// What the take-over confirmation says confirming does to the claim it replaces (`from`): this
// admin (`uid`) claiming it ('claim'), or claiming it and starting work ('progress'), or assigning
// it to `assignee` ('assign'). A developer's agents work on any claim that developer holds as a
// person, assigned ones included, so an agent whose developer becomes the holder isn't stopped:
// only disconnecting it in Connected Devices stops it. Any other new holder stops the agent.
export const takeoverOutcomeText = (
	then: 'claim' | 'progress' | 'assign',
	from: ReportHolder,
	uid: string,
	assignee: { uid: string; name: string } | null = null,
): string => {
	// who holds it once confirmed: the admin it's assigned to, or this admin
	const to: { uid: string; name: string } | null = then === 'assign' ? assignee : null;
	const agent: boolean = from.kind === 'agent';
	const outcome: string = to
		? `Assigning it to ${to.name} takes it from ${agent ? 'the agent' : 'them'}`
		: `Taking it over makes it yours${then === 'progress' ? ' and starts work on it' : ''}`;
	if (!agent) return outcome;
	if (from.uid !== (to ? to.uid : uid)) return `${outcome}. The agent is stopped from changing it, and is told who has it`;
	return to
		? `${outcome}. Their agents can still work on it, this one included, until they disconnect it in Connected Devices`
		: `${outcome}. Your agents can still work on it, this one included: to stop it, disconnect it in Connected Devices`;
};

// Moving a report nobody approved into one of these approves it, as the admin moving it.
const APPROVES_UNAPPROVED: FeedbackReportStatus[] = ['progress', 'fixed', 'merged', 'deployed'];

// A status change. closedAt follows it: stamped as the report closes, kept while it stays closed
// (fixed to merged to deployed included), cleared as it reopens. Approving it records who
// approved it and when, and so does starting, fixing, merging or deploying a report nobody
// approved (the admin doing it approves it: firestore.rules wants an approval on anything moving
// into approved or progress). Leaving duplicate drops the report it repeated. Starting it claims
// it for `claimant` when they pass one (nobody else holds it, or they've chosen to take it over);
// closing it releases whoever held it.
export const statusUpdate = (
	report: FeedbackReport,
	to: FeedbackReportStatus,
	uid: string,
	stamp: unknown,
	claimant: { uid: string; name: string } | null = null,
	takeoverOf: unknown = null,
): FeedbackUpdate => {
	const changes: FeedbackUpdate = { status: to };
	if (isFeedbackReportClosed(to)) {
		if (!isFeedbackReportClosed(report.status) || !report.closedAt) changes.closedAt = stamp;
		// always, not only when this copy shows a holder: an agent may have claimed it a moment ago
		// (firestore.rules wants a report that closes released)
		Object.assign(changes, claimChanges(null, stamp));
	} else {
		changes.closedAt = null;
	}
	if (to === 'progress' && claimant) Object.assign(changes, claimChanges(claimant, stamp, takeoverOf));
	// where a question was asked from: answering one asked during approved work sends it back
	if (to === 'needs_info') changes['triage.askedFrom'] = report.status;
	if (to === 'approved' || (APPROVES_UNAPPROVED.includes(to) && !report.triage?.approvedBy)) {
		changes['triage.approvedBy'] = uid;
		changes['triage.approvedAt'] = stamp;
	}
	if (to !== 'duplicate' && report.triage?.duplicateOf != null) changes['triage.duplicateOf'] = null;
	return triageUpdate(report, changes, stamp);
};

export type FeedbackCommentWrite = Omit<FeedbackReportComment, 'id' | '_created' | '_updated' | 'createdAt'> & { createdAt: unknown };

// A status change's line, with the note that went with it ('' for none).
export const statusComment = (from: FeedbackReportStatus, to: FeedbackReportStatus, author: { uid: string; name: string }, stamp: unknown, note: string = ''): FeedbackCommentWrite => ({
	kind: 'status',
	body: note.trim(),
	from,
	to,
	parentId: null,
	replyCount: 0,
	editedAt: null,
	author: { uid: author.uid, name: author.name, kind: 'human' },
	createdAt: stamp,
});

// A comment, or a reply to the top-level comment or status line `parentId` (the page also adds one
// to its parent's replyCount, in the same batch: firestore.rules wants both).
export const textComment = (body: string, author: { uid: string; name: string }, stamp: unknown, parentId: string | null = null): FeedbackCommentWrite => ({
	kind: 'comment',
	body: body.trim(),
	from: null,
	to: null,
	parentId,
	replyCount: 0,
	editedAt: null,
	author: { uid: author.uid, name: author.name, kind: 'human' },
	createdAt: stamp,
});

export interface FeedbackThread {
	comment: FeedbackReportComment // a top-level comment or status line
	replies: FeedbackReportComment[] // oldest first
}

// The comments as threads: each top-level one with its replies, both in the order given (oldest
// first). A reply whose parent isn't there (it shouldn't happen) stands on its own.
export const threadComments = (comments: FeedbackReportComment[]): FeedbackThread[] => {
	const ids: Set<string> = new Set(comments.map((comment) => comment.id));
	// a Map, not an object: a comment's id can be any string, 'constructor' too
	const replies: Map<string, FeedbackReportComment[]> = new Map();
	comments.forEach((comment) => {
		const parentId: string | null | undefined = comment.parentId;
		if (!parentId || !ids.has(parentId)) return;
		const list: FeedbackReportComment[] | undefined = replies.get(parentId);
		if (list) list.push(comment);
		else replies.set(parentId, [comment]);
	});
	return comments
		.filter((comment) => !comment.parentId || !ids.has(comment.parentId))
		.map((comment) => ({ comment, replies: replies.get(comment.id) || [] }));
};

// What stays in the comment box once `sent` has posted: the box is locked while it sends, but a
// reply whose parent was deleted meanwhile is moved into it, after what was sent.
export const draftAfterSend = (draft: string, sent: string): string =>
	(draft.startsWith(sent) ? draft.slice(sent.length).replace(/^\s+/, '') : draft);

// Copy for agent's snackbar: success, or every way the copy fell short.
export const copiedMessage = (
	number: number,
	problems: { links: string, commentsFailed: boolean, commentsCached: boolean },
): { ok: boolean, warnOnly: boolean, message: string } => {
	const n: string = `#${number}`;
	const comments: string = problems.commentsFailed
		? 'Its comments could not be loaded.'
		: (problems.commentsCached ? 'Its comments may be out of date: the server hasn\'t answered yet.' : '');
	if (!problems.links && !comments) return { ok: true, warnOnly: false, message: `Copied ${n} for your agent. Its links work for an hour.` };
	return {
		ok: false,
		warnOnly: !problems.links && !problems.commentsFailed,
		message: [problems.links ? `Copied ${n} without links to its files. ${problems.links}` : `Copied ${n}.`, comments].filter(Boolean).join(' '),
	};
};

// The length rule for a comment, reply, edit or status note. It counts what is saved, the text
// trimmed, so it agrees with the button that sends it.
export const commentLengthRule = (value: string | null | undefined): boolean | string =>
	(value || '').trim().length <= FEEDBACK_REPORT_COMMENT_MAX_LENGTH || `Must be ${FEEDBACK_REPORT_COMMENT_MAX_LENGTH} characters or fewer`;

// A pull request link is kept only as an http(s) URL.
export const isPullRequestUrl = (value: string): boolean => /^https?:\/\/\S+$/.test((value || '').trim()) && value.trim().length <= 500;

// The board's flight recorder, as the board packs it (web/board/src/feedback/report.ts): what the
// page shows of it.
export interface FeedbackStep {
	at: number
	kind: string
	label: string
	detail?: string
}

export interface FeedbackBundle {
	steps: FeedbackStep[] // the last BUNDLE_LIMITS.steps of them
	errors: FeedbackStep[] // the last BUNDLE_LIMITS.errors of them
	stepCount: number // how many the bundle held
	errorCount: number
}

// What the page reads of a bundle at most. Anyone signed in can send one, and a crafted bundle
// can hold 100,000 entries in a few compressed megabytes: drawn in full, they freeze the tab. The
// board's own recorder keeps a few hundred steps, so real reports fit well inside these.
export const BUNDLE_LIMITS = {
	steps: 2000,
	errors: 200,
	label: 500, // characters
	detail: 2000,
	kind: 40,
};

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);

const clip = (text: string, max: number): string => (text.length > max ? text.slice(0, max) + '...' : text);

// The readable entries of a list, the last `max` of them, each cut to size.
const readSteps = (value: unknown, max: number): { steps: FeedbackStep[], count: number } => {
	const all: unknown[] = Array.isArray(value) ? value : [];
	const steps: FeedbackStep[] = all.slice(-max)
		.filter((step: unknown): step is Record<string, unknown> => isRecord(step) && typeof step.at === 'number' && typeof step.label === 'string')
		.map((step: Record<string, unknown>) => ({
			at: step.at as number,
			kind: typeof step.kind === 'string' ? clip(step.kind, BUNDLE_LIMITS.kind) : '',
			label: clip(step.label as string, BUNDLE_LIMITS.label),
			...(typeof step.detail === 'string' ? { detail: clip(step.detail, BUNDLE_LIMITS.detail) } : {}),
		}));
	return { steps, count: all.length };
};

export const parseBundle = (json: unknown): FeedbackBundle => {
	const steps = readSteps(isRecord(json) ? json.steps : null, BUNDLE_LIMITS.steps);
	const errors = readSteps(isRecord(json) ? json.errors : null, BUNDLE_LIMITS.errors);
	return { steps: steps.steps, errors: errors.steps, stepCount: steps.count, errorCount: errors.count };
};

// The steps' times, against the moment the report was sent: "-1:05".
export const relativeTime = (at: number, sentAt: number): string => {
	const seconds: number = Math.round((at - sentAt) / 1000);
	const sign: string = seconds < 0 ? '-' : '+';
	const abs: number = Math.abs(seconds);
	return `${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')}`;
};

// A stored time as "2026-10-30 17:05 UTC".
const isoMinute = (value: { toMillis (): number } | null | undefined): string =>
	value ? new Date(value.toMillis()).toISOString().slice(0, 16).replace('T', ' ') + ' UTC' : 'unknown';

// What getFeedbackFileLinks answers (GetFeedbackFileLinksOnCallResult in firebase/functions/src/types).
export type FeedbackFileLinksResult =
	{ success: true, shots: string[], bundle: string | null, expiresAt: number } |
	{ success: false, message: string };

export interface AgentReportLinks {
	shots: string[] // a link per screenshot, in order; empty when none could be made
	bundle: string | null
	expiresAt: number | null // when the links stop working (ms epoch); null without links
}

// Deleted by its author: it stays in the thread, its words hidden.
export const isCommentDeleted = (comment: FeedbackReportComment): boolean => !!comment.deletedAt;

// Waiting on a question asked while it was approved work (approved or in progress), so answering
// it sends it back to work, as the feedback API reads it (is_work in restServer
// apiv2/feedback_service.py). A report a person sent back to review keeps its old approval on
// the page, so the approval alone doesn't say this.
export const askedDuringWork = (report: FeedbackReport, comments: FeedbackReportComment[]): boolean => {
	if (report.status !== 'needs_info' || !report.triage?.approvedBy) return false;
	const askedFrom: FeedbackReportStatus | null | undefined = report.triage.askedFrom;
	if (askedFrom) return askedFrom === 'approved' || askedFrom === 'progress';
	// a question asked before askedFrom was kept: its status line says where from
	for (let i = comments.length - 1; i >= 0; i--) {
		const comment: FeedbackReportComment = comments[i];
		if (comment.kind !== 'status') continue;
		return comment.to === 'needs_info' && (comment.from === 'approved' || comment.from === 'progress');
	}
	return false;
};

const commentLine = (comment: FeedbackReportComment): string => {
	if (isCommentDeleted(comment)) return `${comment.author.name} (${isoMinute(comment.createdAt)}): (comment deleted)`;
	const edited: string = comment.editedAt ? ', edited' : '';
	if (comment.kind === 'status') {
		const moved: string = `${comment.author.name} moved it from ${comment.from ? STATUS_LABELS[comment.from] : '?'} to ${comment.to ? STATUS_LABELS[comment.to] : '?'}`;
		return `${moved} (${isoMinute(comment.createdAt)})${comment.body ? `: ${comment.body}` : ''}`;
	}
	return `${comment.author.name} (${isoMinute(comment.createdAt)}${edited}): ${comment.body}`;
};

// The report as Markdown, for pasting into a developer's agent session. The reporter's email is
// left out. What a report doesn't have (a board, a build, a browser) is left out too, and one an
// agent filed or imported says who filed it and what its list says of it. The screenshot and
// bundle links are signed for an hour (getFeedbackFileLinks): whoever holds them can read the
// files until then. Without links, the files' storage paths stand in. `names` names the people it
// knows by User.id (who assigned it to its holder); anyone else is named by their id.
export const reportForAgent = (report: FeedbackReport, comments: FeedbackReportComment[], links: AgentReportLinks, names: Record<string, string> = {}): string => {
	const lines: string[] = [];
	const priority: FeedbackReportPriority | null | undefined = report.triage?.priority;
	lines.push(`# Feedback #${report.number}: ${KIND_LABELS[report.kind]} (${STATUS_LABELS[report.status]}${priority ? `, ${PRIORITY_LABELS[priority]}` : ''})`);
	lines.push('');
	const kind: FeedbackReportOriginKind | null = originKind(report);
	if (kind) {
		// filed by a developer's agent, or imported from a list: who did it, and what the list says
		const origin: FeedbackReportOrigin | null = report.origin || null;
		const link: string | null = httpLink(origin?.link);
		const who: string = origin?.name || 'a developer\'s agent';
		if (kind === 'import') {
			lines.push(`- Imported: ${isoMinute(report.createdAt)} by ${who}, from ${origin?.list ? `the list "${origin.list}"` : 'a list'}${origin?.externalId ? `, row ${origin.externalId}` : ''}`);
		} else {
			lines.push(`- Filed: ${isoMinute(report.createdAt)} by ${who} (an agent)`);
		}
		if (origin?.reportedBy) lines.push(`- Reported by: ${origin.reportedBy}`);
		if (origin?.reportedAt) lines.push(`- Reported at: ${origin.reportedAt} (as the list gives it)`);
		if (origin?.externalStatus) lines.push(`- Status in the list: ${origin.externalStatus}`);
		if (link) lines.push(`- Link: ${link}`);
	} else {
		lines.push(`- Sent: ${isoMinute(report.createdAt)} by ${isAdminReport(report) ? 'a RunDiffusion admin' : 'a user'}${report.reporter?.companyName ? ` at ${report.reporter.companyName}` : ''}, ${arrival(report).via}`);
	}
	// what isn't known is left out: a report an agent filed or imported has no board, and may have
	// no build or browser (null, or an empty placeholder)
	if (hasBoard(report.board)) lines.push(`- Board: "${report.board.title || 'Untitled'}" (${report.board.id})`);
	if (hasBuild(report.build)) lines.push(`- Build: ${report.build.version || 'unknown'}${report.build.commit ? ` (${report.build.commit})` : ''}`);
	if (hasEnv(report.env)) {
		const screen: string = hasViewport(report.env) ? `, ${report.env.viewport.w}x${report.env.viewport.h} at ${report.env.dpr}x` : '';
		lines.push(`- Browser: ${report.env.browser || 'unknown'} on ${report.env.os || 'unknown'}${screen}${report.env.gpu ? `, GPU ${report.env.gpu}` : ''}`);
	}
	const holder: ReportHolder | null = currentHolder(report);
	if (holder) {
		const assigned: string = holder.assignedBy ? ` (assigned by ${names[holder.assignedBy] || holder.assignedBy})` : '';
		lines.push(`- Claimed by: ${holder.name || holder.uid}${holder.kind === 'agent' ? ' (an agent)' : ''}${assigned}${holder.since ? ` since ${holder.since.toISOString().slice(0, 16).replace('T', ' ')} UTC` : ''}`);
	}
	if (report.triage?.pr) lines.push(`- Pull request: ${report.triage.pr}`);
	if (report.triage?.duplicateOf != null) lines.push(`- Duplicate of #${report.triage.duplicateOf}`);
	lines.push('');
	lines.push(`## ${KIND_QUESTIONS[report.kind].happened}`);
	lines.push(report.happened);
	if (report.expected) {
		lines.push('');
		lines.push(`## ${KIND_QUESTIONS[report.kind].expected}`);
		lines.push(report.expected);
	}
	if ((report.shots.length || report.bundle) && links.expiresAt) {
		lines.push('');
		lines.push(`The links below work until ${isoMinute({ toMillis: () => links.expiresAt as number })}, an hour from when this was copied.`);
	}
	if (report.shots.length) {
		lines.push('');
		lines.push('## Screenshots');
		report.shots.forEach((shot, i) => {
			const where: string = [
				`${shot.w}x${shot.h}`,
				shot.camera ? `camera ${Math.round(shot.camera.x)},${Math.round(shot.camera.y)} zoom ${shot.camera.zoom}` : '',
				shot.selection.length ? `selected ${shot.selection.join(', ')}` : '',
				shot.panels.length ? `open: ${shot.panels.join(', ')}` : '',
			].filter(Boolean).join('; ');
			lines.push(`${i + 1}. ${links.shots[i] || shot.path} (${where})`);
		});
	}
	if (report.bundle) {
		lines.push('');
		lines.push('## Recent activity');
		lines.push(`${report.counts.steps} steps and ${report.counts.errors} errors, in ${report.bundle.gzip ? 'a gzipped' : 'a'} JSON bundle (steps, errors, frame times, board state, element snapshot):`);
		lines.push(links.bundle || report.bundle.path);
	}
	if (comments.length) {
		lines.push('');
		lines.push('## Comments and history');
		threadComments(comments).forEach((thread) => {
			lines.push(`- ${commentLine(thread.comment)}`);
			thread.replies.forEach((reply) => lines.push(`  - ${commentLine(reply)}`));
		});
	}
	return lines.join('\n');
};
